import React, { createContext, useContext, useEffect, useState } from "react";
import {
  Stepper,
  StepperContent,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperPanel,
  StepperSeparator,
  StepperTrigger,
} from "@/components/ui/stepper";
import { Button } from "@/components/ui/button";
import { Outlet, Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { StepperNavigationButtons } from "components/common/stepper/StepperNavigationButtons"

// Types
export type StepType = {
  id: number;
  pathName: string;
  component:  React.LazyExoticComponent<() => JSX.Element> ;
  name?: string;
  title?: string;
  description?: string;
};

// Context pour les boutons de navigation
interface StepperContextType {
  currentStepIndex: number;
  totalSteps: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  goToNext: (nextRoute: string|undefined) => void;
  goToPrevious: () => void;
  goToStep: (stepId: number) => void;
  isNavigating: boolean;
  setIsNavigating: (value: boolean) => void;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const useStepperNavigation = () => {
  const context = useContext(StepperContext);
  if (!context) {
    throw new Error("useStepperNavigation must be used within CustomStepper");
  }
  return context;
};

// Composant CustomStepper principal
function CustomStepper({
   steps,
   className = "",
   showDefaultButtons = false,
 }: {
  steps: StepType[];
  className?: string;
  showDefaultButtons?: boolean;
}) {
  const [currentStepIndex, setCurrentStepIndex] = useState(1);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentStep = steps.find((s) => s.id === currentStepIndex);
  const isFirstStep = currentStepIndex === 1;
  const isLastStep = currentStepIndex === steps.length;

  const handleStepChange = (step: number, nextRoute?: string | undefined) => {
    const selectedStep = steps.find((s) => s.id === step);
    if (selectedStep) {
      setCurrentStepIndex(step);
      // console.log("Navigate to Step", step, "route", nextRoute);
      navigate(nextRoute ?? selectedStep.pathName);
    }
  };

  const goToNext = ( nextRoute: string| undefined) => {
    if (currentStepIndex < steps.length) {
      handleStepChange(currentStepIndex + 1, nextRoute)
    } else if (currentStepIndex === steps.length) {
      navigate("/")
    }
  }

  const goToPrevious = () => {
    if (currentStepIndex > 1) {
      handleStepChange(currentStepIndex - 1);
    }
  };

  const goToStep = (stepId: number) => {
    if (stepId >= 1 && stepId <= steps.length) {
      handleStepChange(stepId);
    }
  };

  // Synchroniser avec l'URL actuelle
  useEffect(() => {
    const currentPath = location.pathname.split("/").at(-1);
    const matchingStep = steps.find((s) => s.pathName.includes(currentPath as string));
    if (matchingStep && matchingStep.id !== currentStepIndex) {
      setCurrentStepIndex(matchingStep.id);
    }
  }, [location.pathname, steps]);

  // Navigation initiale
  // useEffect(() => {
  //   if (currentStep) {
  //     navigate(currentStep.pathName);
  //   }
  // }, []);

  const contextValue: StepperContextType = {
    currentStepIndex,
    totalSteps: steps.length,
    isFirstStep,
    isLastStep,
    goToNext,
    goToPrevious,
    goToStep,
    isNavigating,
    setIsNavigating,
  };

  return (
    <StepperContext.Provider value={contextValue}>
      <div className={`flex flex-col gap-5 p-6 md:p-10 w-full mx-auto max-w-[900px] ${className}`}>
        <Stepper
          value={currentStepIndex}
          onValueChange={handleStepChange}
          className="space-y-8"
        >
          <StepperNav className="[&>*:not(:last-child)]:w-full">
            {steps.map((step, index) => (
              <StepperItem
                key={step.id}
                step={step.id}
                completed={currentStepIndex > step.id}
              >
                <StepperTrigger>
                  <StepperIndicator className="data-[state=completed]:bg-blue-700 data-[state=completed]:text-white data-[state=active]:bg-blue-600 data-[state=active]:text-primary-foreground data-[state=inactive]:text-muted-foreground">
                    {step.id}
                  </StepperIndicator>
                  {step.title && (
                    <div className="ml-2 flex flex-col items-start">
                      <span className="text-sm font-medium">{step.title}</span>
                      {step.description && (
                        <span className="text-xs text-muted-foreground">
                          {step.description}
                        </span>
                      )}
                    </div>
                  )}
                </StepperTrigger>
                {index < steps.length - 1 && (
                  <StepperSeparator className="data-[state=completed]:bg-blue-700" />
                )}
              </StepperItem>
            ))}
          </StepperNav>

          <StepperPanel className="text-sm">
            {steps.map((step) => (
              <StepperContent
                className="w-full flex items-center justify-center"
                key={step.id}
                value={step.id}
              >
                <div className="w-[600px] flex flex-col items-center gap-x-2">
                  <div className="text-center text-lg font-semibold mb-4">{step.name}</div>
                  <Outlet />
                </div>
              </StepperContent>
            ))}
          </StepperPanel>

          {showDefaultButtons && (
            <StepperNavigationButtons />
          )}
        </Stepper>

        {/* Routes */}
        {/*<Routes>*/}
        {/*  {steps.map((step, index) => (*/}
        {/*    <Route*/}
        {/*      key={step.id}*/}
        {/*      index={index === 0}*/}
        {/*      path={step.pathName}*/}
        {/*      element={<step.component />}*/}
        {/*    />*/}
        {/*  ))}*/}
        {/*</Routes>*/}
      </div>
    </StepperContext.Provider>
  );
}

export default CustomStepper;